/**
 * Layout API — scope-namespaced HTTP calls for the layout-store.
 *
 * Same pattern as `workspace-api.ts` — one-line consumers over the
 * `usersClient` singleton. Auth + 401-refresh shared session-wide.
 */

import { usersClient } from '@/services/api/api-client'
import type { FullLayoutData } from './types'
import { recordDump } from '@/services/api/save-dump'

function layoutPath(scope: string, layoutId: string): string {
  return `/v2/workspace/${scope}/layouts/${layoutId}`
}

export async function loadLayout(scope: string, layoutId: string): Promise<FullLayoutData | null> {
  try {
    return await usersClient.get<FullLayoutData | null>(layoutPath(scope, layoutId))
  } catch {
    return null
  }
}

export async function saveLayout(
  scope: string,
  layoutId: string,
  data: FullLayoutData,
  opts?: { revisioned?: boolean },
): Promise<{ updatedAt: string; layoutRevision?: number }> {
  const path = layoutPath(scope, layoutId)
  const body = { ...data, revisioned: opts?.revisioned ?? true }
  recordDump(scope, 'saveLayout', path, { layoutId, ...body })
  // PUT throws ConflictError on 409 automatically — let it propagate.
  // layoutRevision comes back only on revisioned saves (non-revisioned
  // saves don't bump it, and the server won't echo a fabricated value).
  return usersClient.put<{ updatedAt: string; layoutRevision?: number }>(path, body)
}
